<template>
  <v-dialog v-model="dialogVisible" max-width="560" persistent>
    <v-card>
      <v-card-title class="py-3">
        {{ $t('createOrderFromOffer.title') }}
      </v-card-title>

      <v-card-text>
        <!-- Krok 1: wybór roli -->
        <div v-if="step === 1">
          <p class="body-1 mb-4">{{ $t('createOrderFromOffer.selectRolePrompt') }}</p>
          <v-btn-toggle v-model="selectedRole" mandatory class="d-flex">
            <v-btn value="client" class="flex-grow-1" outlined>
              <v-icon left>mdi-account</v-icon>
              {{ $t('createOrderFromOffer.roleClient') }}
            </v-btn>
            <v-btn value="carrier" class="flex-grow-1" outlined>
              <v-icon left>mdi-truck</v-icon>
              {{ $t('createOrderFromOffer.roleCarrier') }}
            </v-btn>
          </v-btn-toggle>
        </div>

        <!-- Krok 2: sprawdzanie -->
        <div v-if="step === 2" class="d-flex flex-column align-center py-4">
          <v-progress-circular indeterminate color="primary" size="48" />
          <p class="mt-4 body-1">{{ $t('createOrderFromOffer.checking') }}</p>
        </div>

        <!-- Krok 3: wynik -->
        <div v-if="step === 3">
          <v-alert
            v-if="foundContractor"
            type="success"
            text
            class="mb-4"
          >
            {{ $t('createOrderFromOffer.contractorFound') }}:
            <strong>{{ foundContractor.companyName }}</strong>
          </v-alert>

          <v-alert
            v-else
            type="warning"
            text
            class="mb-4"
          >
            {{ $t('createOrderFromOffer.contractorNotFound') }}
          </v-alert>

          <div class="body-2 mb-2">
            <strong>{{ offer.companyName }}</strong>
          </div>
          <div v-if="offer.vatNumber" class="body-2 lightText--text mb-1">
            NIP: {{ offer.vatNumber }}
          </div>
          <div v-if="offer.city" class="body-2 lightText--text">
            {{ offer.zipCode }} {{ offer.city }}, {{ offer.country }}
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="px-6 pb-4 d-flex justify-end">
        <v-btn
          outlined
          :disabled="step === 2"
          @click="close"
        >
          {{ $t('common.cancel') }}
        </v-btn>

        <!-- Krok 1: Dalej -->
        <v-btn
          v-if="step === 1"
          color="primary"
          depressed
          class="ml-2"
          @click="checkContractor"
        >
          {{ $t('common.next') }}
        </v-btn>

        <!-- Krok 3: Kontrahent znaleziony → Otwórz formularz zlecenia -->
        <v-btn
          v-if="step === 3 && foundContractor"
          color="primary"
          depressed
          class="ml-2"
          @click="openOrderForm"
        >
          {{ $t('createOrderFromOffer.openOrderForm') }}
        </v-btn>

        <!-- Krok 3: Brak kontrahenta → Dodaj -->
        <v-btn
          v-if="step === 3 && !foundContractor"
          color="primary"
          depressed
          class="ml-2"
          @click="goToAddContractor"
        >
          {{ $t('createOrderFromOffer.addContractor') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Service from '@/services'

export default {
  name: 'CreateOrderFromOfferDialog',
  props: {
    value: {
      type: Boolean,
      required: true
    },
    offer: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      step: 1,
      selectedRole: 'client',
      foundContractor: null
    }
  },
  computed: {
    dialogVisible: {
      get () { return this.value },
      set (val) { this.$emit('input', val) }
    }
  },
  watch: {
    value (newVal) {
      if (newVal) {
        this.step = 1
        this.selectedRole = 'client'
        this.foundContractor = null
      }
    }
  },
  methods: {
    async checkContractor () {
      this.step = 2
      this.foundContractor = null

      try {
        const contractorType = this.selectedRole === 'client' ? 1 : 2
        const { data } = await Service.get('/TSLContractor/GetContractors', {
          type: contractorType,
          search: this.offer.vatNumber,
          page: 1,
          pageSize: 10
        })

        const exactMatch = (data.results || []).find(
          c => c.vatNumber === this.offer.vatNumber
        )

        this.foundContractor = exactMatch || null
      } catch (e) {
        this.foundContractor = null
      } finally {
        this.step = 3
      }
    },

    buildPlaces () {
      const places = []
      let order = 0

      ;(this.offer.loadingPlaces || []).forEach(p => {
        places.push({
          location: {
            country: p.country || null,
            place: {
              postalCode: (p.place && p.place.postalCode) || p.postalCode || '',
              city: (p.place && p.place.city) || p.city || ''
            }
          },
          type: 0,
          order: order++,
          dateTime: p.date || null,
          description: null,
          fix: null,
          weight: null,
          loadMeters: null,
          showTimeSliders: false,
          timeFrom: null,
          timeTo: null,
          dirtTime: false
        })
      })

      ;(this.offer.unloadingPlaces || []).forEach(p => {
        places.push({
          location: {
            country: p.country || null,
            place: {
              postalCode: (p.place && p.place.postalCode) || p.postalCode || '',
              city: (p.place && p.place.city) || p.city || ''
            }
          },
          type: 1,
          order: order++,
          dateTime: p.date || null,
          description: null,
          fix: null,
          weight: null,
          loadMeters: null,
          showTimeSliders: false,
          timeFrom: null,
          timeTo: null,
          dirtTime: false
        })
      })

      return places
    },

    openOrderForm () {
      this.$store.commit('SET_TSL_ORDER_PRE_FILL', {
        contractorId: this.foundContractor.id,
        contractorRole: this.selectedRole,
        places: this.buildPlaces(),
        pendingOrderCreation: false
      })
      this.$emit('input', false)
      this.$router.push({ name: 'AddTSLOrder' })
    },

    goToAddContractor () {
      this.$store.commit('SET_TSL_ORDER_PRE_FILL', {
        contractorId: null,
        contractorRole: this.selectedRole,
        places: this.buildPlaces(),
        pendingOrderCreation: true,
        offerCompanyData: {
          companyName: this.offer.companyName || '',
          vatNumber: this.offer.vatNumber || '',
          street: this.offer.street || '',
          zipCode: this.offer.zipCode || '',
          city: this.offer.city || '',
          country: this.offer.country || null,
          phone: this.offer.phone || '',
          email: this.offer.email || ''
        }
      })
      this.$emit('input', false)

      const contractorRouteType = this.selectedRole === 'client' ? 'client' : 'carrier'
      this.$router.push({
        name: 'AddTSLContractor',
        params: { type: contractorRouteType }
      })
    },

    close () {
      this.$store.commit('SET_TSL_ORDER_PRE_FILL', null)
      this.$emit('input', false)
    }
  }
}
</script>
